import { useContextStore } from "@/store/context";
import { useClientStore } from "@/store/client";
import { useMagasinStore } from "@/store/magasin";
import { useProductDetailsTracking } from "@/mixins/tracking/product-details";
import { getQueryParamsFromUrl } from "@/utils/api-drive-params";
import { useProductsStore } from "@/store/products";
import { useProductSearchStore } from "@/store/product-search";
import { useTrackingDP } from "@/mixins/tracking/page-descente-produits";
import { useTrackingRecherche } from "@/mixins/tracking/page-recherche";

export const useContainerProductList = (
  productsList,
  sortCriteria,
  appliedFilters
) => {
  const route = useRoute();

  const { trackProductPage } = useProductDetailsTracking();

  const { layout } = storeToRefs(useContextStore());
  const { isPro } = storeToRefs(useClientStore());
  const { id: shopId } = storeToRefs(useMagasinStore());

  const { fetchFiltredProductList } = useProductsStore();

  const { fetchFiltredSearchResults } = useProductSearchStore();

  const { trackDP, emitEventPartenaires: emitEventPartenairesDP } =
    useTrackingDP();
  const {
    trackRecherche,
    emitEventPartenaires: emitEventPartenairesRecherche
  } = useTrackingRecherche();

  const fetchFiltredProducts = async (params) => {
    params.shopId = shopId.value;
    params.categoryId = route.params.id;
    params.currentFiltersValue = [...appliedFilters];
    const path = prepareFiltredDPPath(params, route.path);
    await navigateTo(path, { replace: true });
  };

  const loadNextPage = async (params) => {
    const parameters = {
      shopId: shopId.value,
      categoryId: route.params.id,
      isPro: isPro.value
    };
    const queryParameters = getQueryParamsFromUrl(route.params, route.query);

    queryParameters.page = params.page;

    await fetchFiltredProductList({
      ...parameters,
      ...queryParameters
    });

    await fetchFiltredProducts(params);

    trackDP();
    emitEventPartenairesDP();
  };

  const resetFilters = async (params) => {
    params.shopId = shopId.value;
    params.categoryId = route.params.id;
    params.currentFiltersValue = [...appliedFilters];
    params.reset = true;
    const path = prepareFiltredDPPath(params, route.path);
    await navigateTo(path);
  };

  const prepareFiltredDPPath = (params, oldPath) => {
    let newPath = oldPath;
    // filter path
    if (params.flag === 1) {
      if (params.currentFiltersValue[0]) {
        newPath = oldPath.substring(0, oldPath.indexOf("/NW-"));
      }
    }
    const path = params.reset
      ? resetPath(params.currentFiltersValue, newPath)
      : getPath(params.filters, oldPath, newPath);
    return {
      path: path,
      query: getQueryParams(params)
    };
  };

  const searchFetchFiltredProducts = async (params) => {
    params.shopId = shopId.value;
    params.currentFiltersValue = [...appliedFilters];
    const path = prepareFiltredSearchPath(params, route.path);
    await navigateTo(path);
  };

  const loadNextSearchPage = async (params) => {
    const parameters = {
      shopId: shopId.value,
      isPro: isPro.value
    };
    const queryParameters = getQueryParamsFromUrl(route.params, route.query);

    queryParameters.page = params.page;

    await fetchFiltredSearchResults({
      ...parameters,
      ...queryParameters
    });

    await searchFetchFiltredProducts(params);

    trackRecherche();
    emitEventPartenairesRecherche();
  };
  const resetSearchFilters = async (params) => {
    params.shopId = shopId.value;
    params.currentFiltersValue = [...appliedFilters];
    params.reset = true;
    const path = prepareFiltredSearchPath(params, route.path);
    await navigateTo(path);
  };

  const prepareFiltredSearchPath = (params, oldPath) => {
    let newPath = oldPath;
    // filter path
    if (params.flag === 1) {
      if (params.currentFiltersValue[0]) {
        newPath = oldPath.substring(0, oldPath.indexOf("/skus"));
      }
    }
    const path = params.reset
      ? encodeURI(
          resetPath(params.currentFiltersValue, decodeURIComponent(newPath))
        )
      : encodeURI(
          getPath(params.filters, oldPath, decodeURIComponent(newPath))
        );

    return {
      path: path,
      query: getQueryParams(params)
    };
  };

  const getQueryParams = (params) => {
    const dpQuery = {};
    const activeSortCriteria =
      sortCriteria && sortCriteria.find((element) => element.actif === true);
    if (params.sortBy && params.sortBy.value && !params.sortBy.isDefault) {
      dpQuery.sortby = params.sortBy.value;
    } else if (activeSortCriteria && !activeSortCriteria.defaut) {
      if ((params.sortBy && !params.sortBy.isDefault) || !params.sortBy) {
        dpQuery.sortby = activeSortCriteria.valeur;
      }
    }
    if (params.page && params.page !== 1) {
      dpQuery.pageindex = params.page;
    }
    if (route.query && route.query.keywords) {
      dpQuery.keywords = route.query.keywords;
    }
    return dpQuery;
  };

  const getPath = (filters, oldPath, newPath) => {
    let path = newPath;
    if (filters) {
      for (const appliedFilter of filters) {
        if (appliedFilter.checked) {
          if (appliedFilter.slider) {
            const oldSliderFilterValue = appliedFilters.find(
              (filter) => filter.code === appliedFilter.code
            );
            path = oldSliderFilterValue
              ? oldPath.replace("/" + oldSliderFilterValue.valeur, "")
              : path;
          }
          path =
            path.replace(".html", "") +
            "/" +
            encodeURIComponent(appliedFilter.value);
        } else {
          path = path.split("%2b").join("+").split("%2B").join("+"); // for energitic classe
          path = path.replace(
            "/" + encodeURIComponent(appliedFilter.value),
            ""
          );
        }
      }
    }

    return path;
  };
  const resetPath = (appliedFilters, newPath) => {
    let path = newPath;
    if (appliedFilters) {
      for (const appliedFilter of appliedFilters) {
        path = path.split("%2b").join("+").split("%2B").join("+"); // for energitic classe
        path = path.replace("/" + appliedFilter.valeur, "");
      }
    }
    return path;
  };

  const onProductIntersect = (index) => {
    if (index % 6 === 0) {
      const products =
        productsList && productsList.length > 0
          ? productsList.slice(index - 6, index)
          : [];
      trackProductPage({
        type: trackingCodes.IMPRESSIONDONE,
        products,
        index,
        // TODO : de où vient ce this.label ?
        actionField: "label"
      });
    }
  };

  return {
    layout,
    isPro,
    shopId,
    fetchFiltredProducts,
    searchFetchFiltredProducts,
    onProductIntersect,
    resetFilters,
    resetSearchFilters,
    loadNextPage,
    loadNextSearchPage
  };
};
